import React, { useContext, useEffect, useRef, useState } from "react"
import styled from "styled-components"
import { Row, Stack } from "react-bootstrap"
import { useRouter } from "next/router"
import AcIcon from "~/components/atoms/AcIcon"
import dayjs from "dayjs"
import { FormProvider, useForm } from "react-hook-form"
import AcButton from "~/components/atoms/AcButton"
import AcModal, { ModalInnerTitle } from "~/components/atoms/AcModal"
import ToggleVideoButton from "~/components/twilio/ToggleVideoButton"
import ToggleAudioButton from "~/components/twilio/ToggleAudioButton"
import EndCallButton from "~/components/twilio/EndCallButton"
import { useGlobal } from "~/composable/useGlobal"
import { OnlineSignContract, SignDocument } from "~/model/Contract"
import DocumentSignInput, {
  DocumentSignInputRef,
  DocumentSignInputValues,
  SignColumnName,
} from "~/components/molecules/DocumentSignInput"
import clone from "rfdc/default"
import { Icon } from "~/components/atoms/styled/Icon"
import ToggleRecordButton from "~/components/twilio/ToggleRecordButton"
import * as Sentry from "@sentry/browser"
import { DocumentType, WorkflowActionId, WorkflowActor } from "~/model/Workflow"
import AcCallVideoHeader from "~/components/molecules/AcCallVideoHeaderZoom"
import { useCallVideoState } from "~/composable/useCallVideoStateZoom"
import ParticipantList from "~/components/twilio/ParticipantList"
import PdfViewerMeeting from "~/components/organisms/PdfViewerMeetingZoom"
import MainParticipant from "~/components/twilio/MainParticipant"
import { checkIsNull, isEmptyObj } from "~/utils/functions"
import { useContract } from "~/composable/useContract"
import { useSelector } from "react-redux"
import { selectActor } from "~/slices/authorizationSlice"
import { useContractDocument } from "~/composable/useContractDocument"
import { GenericActionTypes } from "~/model/ContractDocument"
import { useStateMachine } from "~/composable/useStateMachine"
import { useBlc } from "~/composable/useBlc"
import { VideoContext } from "~/components/twilio/VideoProvider"
import { ParticipantsContext } from "~/components/twilio/ParticipantsProvider"
import { DATA_KEY } from "~/composable/useCallVideoStateZoom"
import { LocalTracksContext } from "~/components/twilio/LocalTracksProvider"

export enum UserType {
  GUEST,
  CONTRACTOR,
  AGENT,
}

enum Mode {
  PROCESS,
  COMPLETE,
  FINISH,
}

interface Props {
  userType: UserType
  setStatus: React.Dispatch<React.SetStateAction<number>>
}

export enum MessageType {
  NOTIFY,
  CHECK,
}

export type Json = { [KEY in string]: any }

export default function Meet(props: Props) {
  const { userType, setStatus } = props
  const isContractor = userType === UserType.CONTRACTOR
  const isAgent = userType === UserType.AGENT
  const router = useRouter()
  const methods = useForm({
    mode: "all",
  })
  const {
    selectId,
    setDocument,
    getPdfUrl,
    setFile,
    setParticipantList,
    setSelectId,
    participantList,
  } = useCallVideoState()
  const [openStartModal, setOpenStartModal] = useState(true)
  const { getContractVideo } = useContract()
  const actor = useSelector(selectActor)
  const { getContractDocumentDetailById } = useContractDocument()
  const { sendAction } = useStateMachine()

  const { showDialog, loading } = useGlobal()
  const {
    connect,
    mediaStream,
    client,
    sendData,
    sessionId,
    leaveSession,
    toggleVideo,
    toggleAudio,
  } = useContext(VideoContext)
  const userId = client?.getCurrentUserInfo()?.userId
  const { toggleRecording, isRecording, setStatusRecording } = useContext(ParticipantsContext)
  const { videoEnabled, audioEnabled } = useContext(LocalTracksContext)
  const { generateSignData } = useBlc()
  const [inputValues, setInputValues] = useState<DocumentSignInputValues>(
    {} as DocumentSignInputValues
  )
  const explanationIndex = useRef(0)
  const columnNamesRef = useRef<SignColumnName>({})
  const [currentSignDocument, setCurrentSignDocument] = useState<SignDocument>({} as SignDocument)
  const [onlineSignContract, setOnlineSignContract] = useState<OnlineSignContract>()
  const [mode, setMode] = useState<Mode>(Mode.PROCESS)
  const [closeModal, setCloseModal] = useState(false)
  const [openRecordModal, setOpenRecordModal] = useState(false)
  const [modal, setModal] = useState({
    title: "",
    isLoading: false,
    errorMessage: "",
    show: false,
    signedTime: "",
  })
  const [currentData, setCurrentData] = useState({})
  const [recordLoading, setRecordLoading] = useState(false)
  const { formState } = methods
  const [isValid, setIsValid] = useState(false)
  const refDocumentSignInput = useRef<DocumentSignInputRef>()
  const [videoMainHeight, setVideoMainHeight] = useState(window.innerHeight)

  /**
   * WebRTCの準備ステータス通知
   * @param state
   */
  const onUpdateState = async (state: { code: number; message: string }) => {
    loading(state.message)
    if (state.code === 4) {
      setTimeout(() => {
        if (isContractor) {
          loading("電子サインの準備をしています。")
        } else {
          loading(false)
        }
      }, 200)
    }
  }

  const explanationStart = async (signDocument: SignDocument): Promise<boolean> => {
    loading("電子サインの準備をしています。")
    setMode(Mode.PROCESS)
    methods.reset()
    methods.clearErrors()
    const values = clone(onlineSignContract)
    delete values.signDocuments

    /**
     * 氏名は除外
     */
    delete values.fullName

    Object.keys(values).forEach((key) => {
      methods.setValue(key, values[key])
    })
    setInputValues({ ...inputValues, ...values })
    setCurrentData(values)
    updateData({
      [DATA_KEY.MODE]: Mode.PROCESS,
      [DATA_KEY.SIGN_DOCUMENT]: signDocument.id,
      [DATA_KEY.INPUT_VALUES]: values,
    })
    return true
  }

  const onNextExplanation = async () => {
    try {
      onModalClose()
      loading(true)
      const nextDocument = onlineSignContract.signDocuments.filter(
        (item) => item.id !== currentSignDocument.id && item.workflowType !== DocumentType.SHARE
      )[0]
      setCurrentSignDocument(nextDocument)
      const result = await explanationStart(nextDocument)
      if (!result) {
        throw new Error("電子サインの準備に失敗しました。")
      }
    } catch (e) {
      showDialog("エラー", e.message, "戻る").then()
      Sentry.captureException(e)
    } finally {
      loading(false)
    }
  }

  const onConfirm = async () => {
    const { address1, ...formFields } = methods.getValues()
    if (await methods.trigger(Object.keys(formFields))) {
      const modalValue = {
        ...modal,
        show: true,
        title: `${currentSignDocument?.workflowName}の確認項目`,
      }
      setModal(modalValue)
      updateData({
        [DATA_KEY.MODAL]: modalValue,
        [DATA_KEY.COLUMN_NAMES_REF]: refDocumentSignInput.current.getSignColumnNames(),
      })
    }
  }

  const showSignError = () => {
    const modalValue = { ...modal, show: false }
    setModal(modalValue)
    updateData({
      [DATA_KEY.MODAL]: modalValue,
      [DATA_KEY.IS_SIGN_ERROR]: true,
    })

    showDialog(
      "エラー",
      [
        "ネットワークエラーが発生しました。",
        "大変申し訳ございませんが「再参加する」ボタンを押下し、",
        "もう一度ビデオ通話に入り直してください。",
      ],
      "再参加する"
    ).then(() => window.location.reload())
  }

  const checkValidSignedItems = (signedItems) => {
    const isInvalidItems = checkIsNull(signedItems) || isEmptyObj(signedItems)
    if (isInvalidItems) {
      showSignError()
      return false
    }
    return true
  }

  const getSignedItems = (signData) => {
    const signDataKeys = Object.keys(signData)
    const columnNames = refDocumentSignInput.current?.getSignColumnNames() ?? columnNamesRef.current

    let result = {}

    for (const key of signDataKeys) {
      if (columnNames[key]?.title) {
        if (columnNames[key]?.required && checkIsNull(signData[key])) {
          showSignError()
          break
        }
        result = { ...result, [columnNames[key]?.title]: signData[key] }
      }
    }

    return result
  }

  const onSign = async () => {
    try {
      const pdfId = currentSignDocument.pdfId
      const modalValue = { ...modal, isLoading: true, signedTime: "" }
      setModal(modalValue)
      updateData({
        [DATA_KEY.MODAL]: modalValue,
      })
      const signData = {
        ...inputValues,
        address1: `${inputValues?.address1 || ""} ${inputValues?.address2 || ""}`.trim(),
        ...(inputValues?.date && { date: dayjs(inputValues.date).format("YYYY年　MM月　DD日") }),
      }

      const items = getSignedItems(signData)
      const isValidSignedItems = checkValidSignedItems(items)
      if (!isValidSignedItems) return

      const infoItems = { "サイン⾏為者": "契約者", ファイル名: currentSignDocument.fileName }

      const { signTime, signItems } = generateSignData(pdfId, items, [], infoItems)

      const contractDocumentDetail = await getContractDocumentDetailById(
        currentSignDocument.contractDocumentId
      )
      if (!contractDocumentDetail.version || contractDocumentDetail.version === 1) {
        throw new Error("契約の登録に失敗しました。")
      }

      const result = await sendAction({
        contractDocumentId: contractDocumentDetail.id,
        input: {
          id: currentSignDocument.id,
          sid: sessionId,
          result: { ...signData, date: signTime },
          pdfId,
          signItems,
        },
        genericAction: GenericActionTypes.SIGN,
        docWorkflowNo: contractDocumentDetail.docWorkflowNo,
        actor,
      })

      if (result) {
        const { signDocuments } = onlineSignContract
        const documentRemainingLength = signDocuments.filter(({ workflowType }) =>
          [DocumentType.IMPORTANT, DocumentType.CONTRACT].includes(workflowType)
        ).length
        const index = onlineSignContract.signDocuments.findIndex(
          (item) => item.id === currentSignDocument.id
        )
        let isDocumentRemaining = documentRemainingLength - (index + 1)
        const modalValue = {
          ...modal,
          errorMessage: "",
          isLoading: false,
          signedTime: result.context.signedTime,
          show: !isDocumentRemaining,
        }
        setModal(modalValue)
        const values = {}
        setInputValues(values)
        if (isDocumentRemaining) {
          setMode(Mode.COMPLETE)
          updateData({
            [DATA_KEY.MODE]: Mode.COMPLETE,
            [DATA_KEY.MODAL]: modalValue,
            [DATA_KEY.INPUT_VALUES]: values,
          })
        } else {
          setMode(Mode.FINISH)
          updateData({
            [DATA_KEY.MODE]: Mode.FINISH,
            [DATA_KEY.MODAL]: modalValue,
            [DATA_KEY.INPUT_VALUES]: values,
          })
        }
      } else {
        throw new Error("契約の登録に失敗しました。")
      }

      // const { signDocuments } = onlineSignContract
      // const documentRemainingLength = signDocuments.filter(({ workflowType }) =>
      //   [DocumentType.IMPORTANT, DocumentType.CONTRACT].includes(workflowType)
      // ).length
      // const index = onlineSignContract.signDocuments.findIndex(
      //   (item) => item.id === currentSignDocument.id
      // )
      // let isDocumentRemaining = documentRemainingLength - (index + 1)
      // const _modalValue = {
      //   ...modal,
      //   errorMessage: "",
      //   isLoading: false,
      //   signedTime: signTime,
      //   show: !isDocumentRemaining,
      // }
      // console.log(`index: ${index}, signDocuments: `, signDocuments)
      // setModal(_modalValue)
      // const values = {}
      // setInputValues(values)
      // if (isDocumentRemaining) {
      //   setMode(Mode.COMPLETE)
      //   updateData({
      //     [DATA_KEY.MODE]: Mode.COMPLETE,
      //     [DATA_KEY.MODAL]: _modalValue,
      //     [DATA_KEY.INPUT_VALUES]: values,
      //   })
      // } else {
      //   setMode(Mode.FINISH)
      //   updateData({
      //     [DATA_KEY.MODE]: Mode.FINISH,
      //     [DATA_KEY.MODAL]: _modalValue,
      //     [DATA_KEY.INPUT_VALUES]: values,
      //   })
      // }
    } catch (e) {
      if (e?.response) {
        const modalValue = { ...modal, errorMessage: e.response.data, isLoading: false }
        setModal(modalValue)
        updateData({ [DATA_KEY.MODAL]: modalValue })
      } else {
        const modalValue = { ...modal, errorMessage: e.message, isLoading: false }
        setModal(modalValue)
        updateData({ [DATA_KEY.MODAL]: modalValue })
      }
      Sentry.captureException(e)
    }
  }

  const onReceive = (response: Json) => {
    if (response.type === MessageType.NOTIFY) {
      const keys = Object.keys(response.data)

      if (keys.includes(DATA_KEY.SIGN_DOCUMENT)) {
        const signDocumentId = response.data.signDocument
        const signDocument = onlineSignContract.signDocuments.find(
          (document) => document.id === signDocumentId
        )
        setCurrentSignDocument(signDocument)
      }

      if (keys.includes(DATA_KEY.COLUMN_NAMES_REF)) {
        columnNamesRef.current = response.data.columnNamesRef
      }

      if (keys.includes(DATA_KEY.MODE)) {
        setMode(response.data.mode)
      }

      if (keys.includes(DATA_KEY.INPUT_VALUES)) {
        if (userId === response.data?.userIdAction) return
        setInputValues(response.data.inputValues)
        setCurrentData(response.data.inputValues)
      }

      if (keys.includes(DATA_KEY.MODAL)) {
        setModal(response.data.modal)
      }

      if (keys.includes(DATA_KEY.RECORD)) {
        setStatusRecording(response.data.record)
      }

      if (keys.includes(DATA_KEY.RECORD_LOADING)) {
        setRecordLoading(response.data.recordLoading)
      }

      if (keys.includes(DATA_KEY.CHOOSE_FILE)) {
        const { name, pdfId, userIdHandle } = response.data[DATA_KEY.CHOOSE_FILE]
        const currentUserId = client?.getCurrentUserInfo()?.userId
        if (userIdHandle === currentUserId) return
        setFile(name, pdfId)
        getPdfUrl(pdfId)
      }

      if (keys.includes(DATA_KEY.PARTICIPANT)) {
        const { participant } = response.data
        setParticipantList((prevState) => {
          const isExistingItem = prevState.find(({ userId }) => userId === participant.userId)
          const newState = isExistingItem ? prevState : prevState.concat(participant)
          sendData({
            type: MessageType.NOTIFY,
            data: {
              [DATA_KEY.PARTICIPANTS]: newState,
            },
          })
          return newState
        })
      }

      if (keys.includes(DATA_KEY.PARTICIPANTS)) {
        const { participants } = response.data
        setParticipantList((prevState) => {
          const merged = [...participants, ...prevState].reduce((acc, curr) => {
            if (!acc.find((item) => item.userId === curr.userId)) {
              acc.push(curr)
            }
            return acc
          }, [] as typeof participants)
          return merged
        })
      }

      if (keys.includes(DATA_KEY.CHANGE_TAB)) {
        setSelectId(response.data[DATA_KEY.CHANGE_TAB].tabId)
      }

      if (keys.includes(DATA_KEY.IS_SIGN_ERROR)) {
        showSignError()
      }
    }
  }

  const updateData = (data: any) => {
    if (!mediaStream) return
    let isValid = JSON.stringify(data?.inputValues) !== JSON.stringify(currentData)
    if (isContractor && isValid) {
      sendData({
        type: MessageType.NOTIFY,
        setUpdateTime: new Date().getTime(),
        data: { ...data, userIdAction: userId },
      })
    }
  }

  const updateRecordData = (data: any) => {
    sendData({
      type: MessageType.NOTIFY,
      setUpdateTime: new Date().getTime(),
      data,
    })
  }

  /**
   * ビデオを終了
   */
  const onClose = () => {
    leaveSession()
    setModal({ ...modal, show: false })
    setCloseModal(false)
    if (userType !== UserType.AGENT) {
      router.reload()
    } else {
      router.push({
        pathname: `/${router.query.version === "2" ? "contracts-next" : "contracts"}/[id]/`,
        query: { id: onlineSignContract.id },
      })
    }
  }

  const handleRecording = async () => {
    setOpenRecordModal(false)
    try {
      const userId = client.getCurrentUserInfo()?.userId
      // HungHV: via api, call BE handler to turn on/off record - BE layer will work with twilio
      if (!userId) return
      setRecordLoading(true)
      // HungHV: Synchronize UI with other agents
      updateRecordData({ [DATA_KEY.RECORD_LOADING]: true })

      // HungHV: update UI
      const status = await toggleRecording()
      // HungHV: Synchronize UI with other agents
      updateRecordData({ [DATA_KEY.RECORD]: status })
    } catch (error) {
      console.log(error)
      Sentry.captureException(error)
    } finally {
      setRecordLoading(false)
      updateRecordData({ [DATA_KEY.RECORD_LOADING]: false })
    }
  }
  const handleToggleRecordButton = () => {
    if (isRecording) {
      setOpenRecordModal(true)
    } else {
      handleRecording()
    }
  }

  const onModalClose = (flag: boolean = false) => {
    setModal({ ...modal, show: false })
    if (flag === true) {
      setMode(Mode.FINISH)
      updateData({
        [DATA_KEY.MODE]: Mode.FINISH,
        [DATA_KEY.MODAL]: {
          show: false,
        },
      })
    } else {
      updateData({
        [DATA_KEY.MODAL]: {
          show: false,
        },
      })
    }
  }

  const load = async () => {
    try {
      const onlineSignContract = await getContractVideo(router.query.hash).then(
        (response) => response.data
      )
      const priority = {
        [DocumentType.IMPORTANT]: 0,
        [DocumentType.CONTRACT]: 1,
      }

      const signDocuments = onlineSignContract.signDocuments.sort((a, b) => {
        const priorityA = priority[a.workflowType] ?? 99
        const priorityB = priority[b.workflowType] ?? 99
        return priorityA - priorityB
      })
      onlineSignContract.signDocuments = signDocuments
      setOnlineSignContract(onlineSignContract)
      setDocument(signDocuments)
      const currentSignDocument = signDocuments.find(
        (doc) =>
          doc.workflowData.some(
            (data) =>
              data.actor === WorkflowActor.CONTRACTOR &&
              data.type === WorkflowActionId.SIGN &&
              !data.result
          ) && [DocumentType.IMPORTANT, DocumentType.CONTRACT].includes(doc.workflowType)
      )
      setCurrentSignDocument(currentSignDocument)
    } catch (e) {
      showDialog("エラー", e.message, "戻る").then()
      Sentry.captureException(e)
    }
  }

  useEffect(() => {
    const { address1, address2 } = methods.getValues()
    const isAdressValid = Boolean(address1) || Boolean(address2)
    setIsValid(formState.isValid && isAdressValid)
  }, [formState])

  useEffect(() => {
    if (mediaStream && isContractor) {
      setTimeout(async () => {
        // The Customer expects that if the BLC call fails, it should retry
        // If it still fails after 3 attempts, a popup should be shown and the user should be navigated back to the previous page
        const maxRetries = 3
        let result = false
        for (let attempt = 0; attempt < maxRetries; attempt++) {
          try {
            result = await explanationStart(currentSignDocument)
            if (result) {
              loading(false)
              break
            } else {
              throw new Error("電子サインの準備に失敗しました。")
            }
          } catch (e) {
            console.error(e)
            if (attempt === maxRetries - 1) {
              showDialog(
                "エラー",
                [
                  "大変申し訳ございません。電子サインの準備に失敗しました。",
                  "一度ビデオ画面を終了してから、再度オンライン重説のご参加をお願いします。",
                ],
                "ビデオ画面を終了する"
              ).then(() => {
                leaveSession()
                setStatus(2)
              })
              Sentry.captureException(e)
            }
          } finally {
            if (attempt === maxRetries - 1) {
              loading(false)
            }
          }
        }
      }, 500)
    }
  }, [mediaStream])

  useEffect(() => {
    if (Object.keys(currentSignDocument).length > 0 && isContractor && participantList.length > 1) {
      const values = clone(onlineSignContract)
      delete values.signDocuments
      delete values.fullName
      Object.keys(values).forEach((key) => {
        methods.setValue(key, values[key])
      })
      sendData({
        type: MessageType.NOTIFY,
        setUpdateTime: new Date().getTime(),
        data: {
          [DATA_KEY.INPUT_VALUES]: values,
        },
      })
    }
  }, [participantList, currentSignDocument])

  useEffect(() => {
    loading(true)

    if (router.isReady) {
      load()
    }
  }, [])

  useEffect(() => {
    if (client && mediaStream) {
      client.on("chat-on-message", (payload) => {
        try {
          const { message } = payload
          onReceive(JSON.parse(message))
        } catch (e) {
          console.error(e)
          Sentry.captureException(e)
        }
      })
    }
  }, [client, mediaStream])

  useEffect(() => {
    if (!onlineSignContract) return
    const { signDocuments } = onlineSignContract
    const documentRemainingLength = signDocuments?.filter(({ workflowType }) =>
      [DocumentType.IMPORTANT, DocumentType.CONTRACT].includes(workflowType)
    )?.length
    if (!documentRemainingLength) return
    onUpdateState({ code: 1, message: "接続を開始しています。" })
    const connecting = () => {
      onUpdateState({ code: 2, message: "ミーティングルームを作成しています。" })
    }
    connect(router.query.hash, connecting)
      .then(() => {
        onUpdateState({ code: 4, message: "準備が完了しました。" })
      })
      .catch(() => {
        onUpdateState({ code: 99, message: "接続に失敗しました。" })
      })
  }, [onlineSignContract])

  useEffect(() => {
    setVideoMainHeight(window.innerHeight)
  }, [window])

  useEffect(() => {
    if (!client) return
    const userId = client.getCurrentUserInfo().userId
    const userTypeNames = ["閲覧者", "契約者", "仲介会社"]
    sendData({
      type: MessageType.NOTIFY,
      data: {
        participant: {
          userId: userId,
          userType: userTypeNames[userType],
        },
      },
    })
  }, [client])

  useEffect(() => {
    setTimeout(() => {
      const checkDocumentSignContent = document.getElementsByClassName("lable-custom")
      Array.from(checkDocumentSignContent).forEach((item) => {
        ;(item as HTMLElement).title = item.textContent || ""
      })
    }, 0)
  }, [modal.show])

  return (
    <>
      <AcCallVideoHeader />
      {onlineSignContract?.signDocuments && (
        <FormProvider {...methods}>
          <VideoMain height={videoMainHeight}>
            {selectId === 1 && (
              <>
                {mediaStream && <MainParticipant />}
                {mediaStream && <ParticipantList />}
              </>
            )}
            {selectId === 2 && mediaStream && <PdfViewerMeeting />}
            {selectId == 3 && (
              <CheckList>
                <CheckListContent>
                  {mode === Mode.PROCESS && (
                    <>
                      <Row>
                        <Notice>
                          <h2>
                            {currentSignDocument?.workflowName}
                            チェックリスト
                          </h2>
                          <p>
                            担当者の指示に従いながら、下記のチェックリストを順番に確認し、チェックを進めてください。
                          </p>
                        </Notice>

                        <DocumentSignInput
                          ref={refDocumentSignInput}
                          key={`${explanationIndex.current}`}
                          signDocument={currentSignDocument}
                          isContractor={isContractor}
                          values={inputValues}
                          isMeeting={true}
                          onChange={(values) => {
                            setInputValues(values)
                            setCurrentData(values)
                            updateData({ [DATA_KEY.INPUT_VALUES]: values })
                          }}
                        />
                      </Row>
                      <Row style={{ marginBottom: 80 }}>
                        {isContractor && (
                          <AcButton
                            className={"button radius btn-conversion"}
                            onClick={onConfirm}
                            disabled={!isValid}
                          >
                            確認画面へ進む
                          </AcButton>
                        )}
                      </Row>
                    </>
                  )}
                  {mode === Mode.COMPLETE && (
                    <CompleteResult>
                      <div className="notice">
                        <div className="check-icon">
                          <CheckIcon />
                        </div>
                        <h2>
                          {currentSignDocument?.workflowName?.replace("書", "")}
                          が完了しました。
                        </h2>
                        <span>
                          契約書については全ての電子サイン行為者が電子サインを完了した時点で
                        </span>
                        <br />
                        <p style={{ marginTop: 30 }}>
                          ご登録いただいているメールアドレス宛に契約書のダウンロードURLを送付します。
                        </p>
                      </div>
                      <CompleteResultButton>
                        {isAgent && (
                          <p className="color-red">
                            続けて賃貸借契約を行う場合は、契約者の方に以下のボタンを押していただいてください。
                          </p>
                        )}
                        <p className="note">
                          続けて、賃貸借契約を行う場合は以下のボタンをクリックしてください。
                        </p>
                        <AcButton
                          className="btn-conversion radius button"
                          onClick={onNextExplanation}
                          disabled={!isContractor}
                        >
                          続けて賃貸借契約へ進む
                        </AcButton>
                      </CompleteResultButton>
                    </CompleteResult>
                  )}

                  {mode === Mode.FINISH && (
                    <CompleteResult>
                      <div>
                        <div className="check-icon">
                          <CheckIcon />
                        </div>
                        <h2>
                          {currentSignDocument?.workflowName?.replace("書", "")}
                          が完了しました。
                        </h2>
                        <p>契約書については全ての電子サイン行為者が電子サインを完了した時点で</p>
                        <p style={{ marginTop: 30 }}>
                          ご登録いただいているメールアドレス宛に契約書のダウンロードURLを送付します。
                        </p>
                      </div>
                    </CompleteResult>
                  )}
                </CheckListContent>
              </CheckList>
            )}
            {selectId !== 1 && (
              <ParticipantListCol>{mediaStream && <ParticipantList />}</ParticipantListCol>
            )}
          </VideoMain>
          <AcModal
            size="lg"
            show={modal.show}
            disabled={userType !== UserType.CONTRACTOR}
            onHide={onModalClose}
            title={modal.title}
            isLoading={modal.isLoading}
            errorMessage={modal.errorMessage}
          >
            <React.Fragment key={"body"}>
              {mode === Mode.PROCESS && !modal.errorMessage && (
                <DocumentSignBody>
                  <ModalInnerTitle>下記の内容で電子サインしますか？</ModalInnerTitle>
                  <DocumentSignInput
                    key={explanationIndex.current}
                    signDocument={currentSignDocument}
                    values={inputValues}
                    isConfirm={true}
                    isMeeting={true}
                    customClassname={"lable-custom"}
                  />
                </DocumentSignBody>
              )}
              {mode === Mode.PROCESS && modal.errorMessage && (
                <SignComplete>
                  <p>処理に失敗しました、最初から操作を行ってください</p>
                </SignComplete>
              )}
              {mode === Mode.FINISH && (
                <SignComplete>
                  <div className="check-icon">
                    <CheckIcon />
                  </div>
                  <p>以下の日時にて、書類へのサインおよび、</p>
                  <p className={"mb-5"}>その内容に対する同意の確認処理が完了しました。</p>
                  <p>{dayjs(modal.signedTime).format("YYYY/MM/DD HH:mm")}</p>
                </SignComplete>
              )}
            </React.Fragment>
            <React.Fragment key={"footer"}>
              {mode === Mode.PROCESS && !modal.errorMessage && (
                <>
                  <AcButton
                    className="btn-back m-1"
                    onClick={onModalClose}
                    disabled={Boolean(userType !== UserType.CONTRACTOR)}
                  >
                    修正する
                  </AcButton>
                  <AcButton
                    className="btn-conversion m-1"
                    onClick={onSign}
                    disabled={userType !== UserType.CONTRACTOR}
                  >
                    電子サインする
                  </AcButton>
                </>
              )}

              {mode === Mode.PROCESS && modal.errorMessage && (
                <AcButton className="btn-conversion radius button" onClick={onModalClose}>
                  終了する
                </AcButton>
              )}

              {mode === Mode.COMPLETE && (
                <>
                  {onlineSignContract?.signDocuments?.length - (explanationIndex.current + 1) >
                    0 && (
                    <>
                      <div style={{ marginBottom: "10px", width: "100%" }}>
                        <AcButton
                          className="btn-conversion radius button"
                          onClick={onNextExplanation}
                          disabled={userType !== UserType.CONTRACTOR}
                        >
                          続けて賃貸借契約へ進む
                        </AcButton>
                      </div>
                      <CloseLink
                        isContractor={isContractor}
                        onClick={(e) => {
                          if (isContractor) {
                            onModalClose()
                          }
                        }}
                      >
                        重要事項説明のみで終了する »
                      </CloseLink>
                    </>
                  )}

                  {!(
                    onlineSignContract?.signDocuments?.length -
                    (explanationIndex.current + 1)
                  ) && (
                    <>
                      <AcButton
                        className="btn-conversion radius button"
                        onClick={onModalClose.bind(
                          this,
                          onlineSignContract?.signDocuments[explanationIndex.current]
                            .workflowType === DocumentType.CONTRACT
                            ? true
                            : false
                        )}
                        disabled={userType !== UserType.CONTRACTOR}
                      >
                        終了する
                      </AcButton>
                    </>
                  )}
                </>
              )}

              {mode === Mode.FINISH && (
                <AcButton
                  className="btn-conversion radius button"
                  onClick={onModalClose}
                  disabled={userType !== UserType.CONTRACTOR}
                >
                  終了する
                </AcButton>
              )}
            </React.Fragment>
          </AcModal>
          <AcModal
            size="lg"
            show={openRecordModal}
            onHide={() => {
              setOpenRecordModal(false)
            }}
            title={"録画の一時停止"}
          >
            <React.Fragment key={"body"}>
              <ModalInnerTitle>録画を一時停止しますが、よろしいでしょうか？</ModalInnerTitle>
            </React.Fragment>
            <React.Fragment key={"footer"}>
              <AcButton className="btn-conversion m-1" onClick={handleRecording}>
                録画一時停止
              </AcButton>
              <AcButton
                className="btn-back m-1"
                onClick={() => {
                  setOpenRecordModal(false)
                }}
              >
                キャンセル
              </AcButton>
            </React.Fragment>
          </AcModal>
          <AcModal
            size="lg"
            show={closeModal}
            onHide={setCloseModal.bind(this, false)}
            title={"ビデオの終了"}
          >
            <React.Fragment key={"body"}>
              <ModalInnerTitle>ビデオ画面を終了しますが、よろしいでしょうか？</ModalInnerTitle>
            </React.Fragment>
            <React.Fragment key={"footer"}>
              <AcButton className="btn-conversion m-1" onClick={onClose}>
                終了
              </AcButton>
              <AcButton className="btn-back m-1" onClick={setCloseModal.bind(this, false)}>
                キャンセル
              </AcButton>
            </React.Fragment>
          </AcModal>
          <AcModal
            size="lg"
            show={openStartModal}
            onHide={() => setOpenStartModal(false)}
            title={
              <StartModalTitle>
                <AcIcon name="question" />
                <span>{isContractor ? "ビデオ重説の注意事項" : "ビデオ重説の使い方"}</span>
              </StartModalTitle>
            }
          >
            <React.Fragment key={"body"}>
              <StartModalBody>
                <p>ビデオ重説中は、以下の2点にご留意ください。</p>
                <p className="tutorial-text">
                  {isContractor
                    ? "①仲介会社さまが画面上部のタブを切り替えることによって、契約者 さまの画面も連動して切り替わります。"
                    : "①画面上部のタブを切り替えることによって、契約者さまの画面も連動して切り替わります。"}
                </p>
                <p className="tutorial-text">
                  {isContractor
                    ? "②「電子サイン」画面に切り替わると契約者さまのみ入力することが可能となります。仲介会社さまは入力不可"
                    : "②契約者さまが電子サインを行う際も、仲介会社さまが画面を切り替える必要がございます。"}
                </p>
                <div className="steps_container">
                  <div className="steps_header">
                    <p>タブの機能説明</p>
                  </div>
                  <div className="steps">
                    <div className="step">
                      <div className="step_content">
                        <AcIcon name="users-rectangle" size="3x" />
                        <p>ビデオ通話</p>
                      </div>
                      <p className="step_text">宅地建物取引士の確認</p>
                    </div>
                    <div className="step">
                      <div className="step_content">
                        <AcIcon name="file-lines" size="3x" />
                        <p>
                          画面共有
                          <br />
                          (書類表示)
                        </p>
                      </div>
                      <p className="step_text">
                        {isContractor
                          ? "必要書類が選択され、画面表示"
                          : "必要書類を選択して、画面共有"}
                      </p>
                    </div>
                    <div className="step">
                      <div className="step_content">
                        <AcIcon name="pen-nib" size="3x" />
                        <p>電子サイン</p>
                      </div>
                      <p className="step_text">内容に合意の上、電子サイン</p>
                    </div>
                  </div>
                </div>
              </StartModalBody>
            </React.Fragment>
            <React.Fragment key={"footer"}>
              <AcButton className="btn-conversion" onClick={() => setOpenStartModal(false)}>
                ビデオ重説をはじめる
              </AcButton>
            </React.Fragment>
          </AcModal>
          <VideoController isAgent={isAgent}>
            <EndCallButton onClick={setCloseModal.bind(this, true)} />
            <Stack direction="horizontal" gap={2}>
              <ToggleVideoButton toggleVideo={toggleVideo} isVideoEnabled={videoEnabled} />

              <ToggleAudioButton toggleAudio={toggleAudio} isAudioEnabled={audioEnabled} />
            </Stack>

            {isAgent ? (
              <ToggleRecordButton loading={recordLoading} onClick={handleToggleRecordButton} />
            ) : (
              <div style={{ width: 80 }} />
            )}
          </VideoController>
        </FormProvider>
      )}
    </>
  )
}

const StartModalTitle = styled.div`
  display: flex;
  justify-content: center;
  svg {
    margin-right: 5px;
    color: #000000;
    font-size: 20px;
    border-radius: 16px;
    border: 1px solid #000000;
    width: 20px;
    padding: 5.5px;
    display: flex;
    justify-content: center;
    align-items: center;
  }
`

const VideoMain = styled.div`
  position: fixed;
  width: 100%;
  top: 56px;
  height: calc(100vh - 56px - 80px);
  background-color: black;
  border: 10px solid #ddddff;
  overflow-y: auto;
  display: grid;
  grid-template-columns: 1fr 275px;
  ::-webkit-scrollbar {
    display: none;
  }
  -ms-overflow-style: none;
  scrollbar-width: none;

  @media screen and (max-width: 1185px) {
    grid-template-columns: 1fr;
  }
  @media screen and (max-width: 920px) {
    top: 100px;
    height: calc(${(props) => props.height}px - 100px - 80px);
    -webkit-height: calc(${(props) => props.height}px - 100px - 80px);
    -moz-height: calc(${(props) => props.height}px - 100px - 80px);
    -ms-height: calc(${(props) => props.height}px - 100px - 80px);
    -o-height: calc(${(props) => props.height}px - 100px - 80px);
  }
`
const VideoController = styled.div`
  display: flex;
  align-items: center;
  height: 80px;
  padding: 10px;
  background-color: #ffffff;
  border-top: 1px solid #dddddd;
  position: fixed;
  bottom: 0;
  z-index: 2;

  ${({ isAgent }) =>
    isAgent
      ? `
  justify-content: center;
  width: 600px;
  left: 50%;
  transform: translateX(-50%);
  @media screen and (max-width: 925px) {
    width: 100%;
  }
  `
      : `
  justify-content: space-between;
  width: 100%;
  
  `}

  div:first-child {
    margin-right: auto;
  }

  div:last-child {
    margin-left: auto;
  }
`
const CheckIcon = styled(Icon.Check)`
  color: #11bc4c;
  width: 90px;
  height: 90px;
  margin-bottom: 50px;
  padding: 3% !important;
`

const SignComplete = styled.div`
  padding: 20px 0;
  text-align: center;

  .fa-check {
    padding: 2%;
  }
`

const CloseLink = styled.div`
  color: #4f4ce6;
  ${(props) => (props.isContractor ? "cursor: pointer" : "")}
`
const StartModalBody = styled.div`
  @media screen and (min-width: 500px) {
    padding: 0 10px;
  }
  color: black;
  text-align: center;
  > p {
    margin-bottom: 30px !important;
  }
  > p:nth-child(1) {
    margin-top: 20px;
  }
  .tutorial-text {
    text-decoration-line: underline;
    -webkit-text-decoration-line: underline;
    text-decoration-color: #ffce00;
    -webkit-text-decoration-color: #ffce00;
    text-decoration-thickness: 4px;
    -webkit-text-decoration-thickness: 4px;
    font-size: 14px;
  }
  .steps_container {
    width: 96%;
    margin: 0 auto;
    height: 250px;
    border: 2px solid #ebeef9;
  }
  .steps_header {
    height: 40px;
    background-color: #ebeef9;
    p {
      line-height: 40px;
    }
  }
  .steps {
    padding: 25px 35px;
    display: flex;
    justify-content: space-between;
  }
  .step {
    width: 200px;
  }
  .step_content {
    padding: 15px;
    height: 130px;
    background-color: #ebeef9;
    border-radius: 20px;
    display: flex;
    justify-content: center;
    flex-direction: column;
    p {
      margin-top: 10px;
    }
  }
  .step_text {
    margin-top: 10px;
    font-size: 13px !important;
  }
  @media only screen and (max-width: 992px) {
    font-size: 13px;
    .steps {
      padding: 10px;
    }
    .step {
      width: 30%;
    }
  }
`

const CompleteResult = styled.div`
  display: flex;
  justify-content: space-between;
  flex-direction: column;
  text-align: center;
  .check-icon {
    text-align: center;
  }

  .fa-check {
    padding: 6%;
  }

  h2 {
    text-align: center;
    font-size: 25px !important;
    font-weight: 600;
    margin-bottom: 60px !important;
  }

  p {
    font-size: 16px;
  }

  .color-red {
    color: #ff0000;
  }

  small {
    font-size: 16px;
    line-height: 2.5rem;
  }

  .notice {
    margin-bottom: 70px;
  }

  @media screen and (max-width: 600px) {
    font-size: 12px;
    h2 {
      font-size: 18px !important;
      margin-bottom: 20px !important;
    }
    .notice {
      margin-bottom: 20px;
    }
    p {
      font-size: 12px;
    }
  }
`
const CompleteResultButton = styled.div`
  background-color: #f5f5f5;
  padding: 30px;
  margin-bottom: 70px;
  .note {
    margin: 30px !important;
  }
  @media screen and (max-width: 600px) {
    padding: 15px;
    .note {
      margin: 15px !important;
    }
  }
`

const CheckList = styled.div`
  background-color: white;
  overflow: auto;
`

const CheckListContent = styled.div`
  max-width: 900px;
  margin: 0 auto;
  padding: 40px 15px 0;

  @media screen and (max-width: 600px) {
    padding-top: 20px;
  }
`

const Notice = styled.div`
  h2 {
    font-size: 20px !important;
    margin-bottom: 20px !important;
    text-indent: 20px;
  }

  p {
    margin-bottom: 25px !important;
  }

  @media screen and (max-width: 600px) {
    h2 {
      font-size: 16px !important;
    }
    p {
      font-size: 14px;
    }
  }
`
const ParticipantListCol = styled.div`
  overflow-y: auto;
  ::-webkit-scrollbar {
    display: none;
  }
  -ms-overflow-style: none;
  scrollbar-width: none;
  @media screen and (max-width: 1185px) {
    display: none;
  }
`
const DocumentSignBody = styled.p`
  .lable-custom {
    display: -webkit-box;
    -webkit-line-clamp: 5;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: normal;
  }
`
